import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/supabaseQuery';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Checks if the current user is eligible for a free trial.
 * Ineligible if they have any prior membership (terminal status),
 * any paid order (amount > 0), or any prior trial order ($0).
 */
export function useTrialEligibility() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.memberships.all, 'trial-eligibility', user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user?.id) return { isTrialEligible: true };

      // Check ANY existing membership (regardless of status) — one-membership-per-user mandate
      const anyMembership = await db.fetchTop<{ id: string }>(
        'users_memberships',
        'id',
        1,
        {
          filters: [
            { column: 'user_id', operator: 'eq' as const, value: user.id },
          ],
        }
      );

      if (anyMembership.length > 0) return { isTrialEligible: false };

      // Check prior paid orders (amount > 0)
      const priorPaid = await db.fetchTop<{ id: string }>(
        'users_order_history',
        'id',
        1,
        {
          filters: [
            { column: 'user_id', operator: 'eq' as const, value: user.id },
            { column: 'amount', operator: 'gt' as const, value: 0 },
            { column: 'payment_status', operator: 'eq' as const, value: 'success' },
          ],
        }
      );

      if (priorPaid.length > 0) return { isTrialEligible: false };

      // Check prior trial orders ($0)
      const priorTrial = await db.fetchTop<{ id: string }>(
        'users_order_history',
        'id',
        1,
        {
          filters: [
            { column: 'user_id', operator: 'eq' as const, value: user.id },
            { column: 'amount', operator: 'eq' as const, value: 0 },
            { column: 'payment_status', operator: 'eq' as const, value: 'success' },
          ],
        }
      );

      if (priorTrial.length > 0) return { isTrialEligible: false };

      return { isTrialEligible: true };
    },
  });
}
